import React from 'react';
import { fromPairs, map, get, isEqual, isEmpty, filter, cloneDeep, forEach, isBoolean, includes, toLower } from 'lodash';
import classNames from 'classnames';

// local Dependencies
import { Form } from 'modules/form';
import { button as Button } from 'modules/button';
import { getLabel } from 'utils/getLabel';
import { Icon } from 'modules/icon-bank';

/**
 * @type { object }
 * @desc A React presentation/dumb view component.
 * @example
 * import { formSection as FormSection } from 'ui/views/widgets/formSection';
 *
 * // render
 * <FormSection propName = 'propValue'/>
 */

const Table = ( props ) => {

    const _subFields = filter( props.subFields, ( field ) => {
        return ! includes( toLower( field.name ), 'other' );
    }  );
    const _values = cloneDeep( props.values );

    forEach( _values, val => {
        val.rxNumber = 'Other' === val.rxNumber ? val.rxNumberOther : val.rxNumber;
        val.orderNumber = 'Other' === val.orderNumber ? val.orderNumberOther : val.orderNumber;
    } );

    const yesNo = {
        true: 'Yes',
        false: 'No'
    };

    return (
        <div className = 'ui-form-section__table'>
            <div className = 'ui-form-section__table__header'>
                { map( _subFields, field => {
                    return (
                        <div key={ field.name } className = 'ui-form-section__table__header__title'>
                            { getLabel( field.title ) }
                        </div>
                    );
                } ) }

                { !isEmpty( props.values ) ? (
                    <div className = 'ui-form-section__table__header__title'>
                        { getLabel( props.actionsTitle ) }
                    </div> ) : null }
            </div>
            { !isEmpty( props.values ) ?
                map( _values, ( value, index ) => {
                    return (
                        <div key={ index } className = 'ui-form-section__table__row'>
                            { map( _subFields, field => {
                                let currentValue = get( value, [ field.name ] );

                                if ( isBoolean( currentValue ) ) {
                                    currentValue = yesNo[ currentValue ];
                                }

                                return <div key={ field.name } className = 'ui-form-section__table__row__title'>{ currentValue }</div>;
                            } ) }

                            { !isEmpty( props.values ) ? (
                                <div className = 'ui-form-section__table__row__title'>
                                    {
                                        map( props.rowActions, ( action ) => {
                                            const actionClassName = classNames( 'ui-form-section__table__row__title__action', {
                                                'ui-form-section__table__row__title__action--not-allowed': 'editRow' === action.name && props.isFormVisible
                                            } );
                                            
                                            return (
                                                <div
                                                    key={ action.name }
                                                    className = { actionClassName }
                                                    onClick = { () => 'editRow' === action.name ? props.onEditRow( index ) : props.onRemoveRow( index ) }
                                                >
                                                    <Icon id = { action.name } width = { 19 } height = { 19 } />
                                                    
                                                </div>
                                            );
                                        } )
                                    }
                                </div>
                            ) : null }
                        </div>
                    );
                } )
                : (
                    <div className = 'ui-form-section__table__no-data'>
                        { getLabel( props.noDataDescription ) }
                    </div>
                ) }
        </div>
    );
 };

export const formSection = ( props ) => {

    const formSectionClassName = classNames( 'ui-form-section', props.className );

    // get error message (ref)
    const errorMessage = get( props.error, 'message', null );
    const hasError = !isEmpty( errorMessage );

    return (
        <div className = { formSectionClassName }>

            <div className = 'ui-form-section__label'>
                { /* label text */ }
                <div className = 'ui-form-section__label__text'>{ getLabel( props.label ) }</div>

                { /* required asterisk */ }
                {
                    props.required ? (
                        <div className = 'ui-form-section__label__asterisk'>*</div>
                    ) : null
                }
            </div>

            <div className = 'ui-form-section__head-button'>
                { /* add button */ }
                <Button
                    type = { 'button' }
                    disabled = { false }
                    outline = { true }
                    size = 'small'
                    onClick = { () => props.onAddButtonClick() }
                    title = { props.addButtonTitle }
                    className = { 'ui-form-section__head-button__add-button' }
                />


                { /* remove button */ }
                { !isEmpty( props.values ) ?
                    <Button
                        type = { 'button' }
                        disabled = { false }
                        outline = { true }
                        size = 'small'
                        onClick = { () => props.onRemoveAllButtonClick() }
                        title = { props.removeAllButtonTitle }
                        className = { 'ui-form-section__head-button__remove-all-button' }
                    />
                    : null }
            </div>

            { props.isFormVisible ?
                <Form
                    key = { 'form-section-form' }
                    fields = { fromPairs( map( props.subFields, ( field ) => [ field.name, field ] ) ) } // convert to object as specified by `Form` prop
                    values = { props.editableValues } // default values
                    isValid = { false }
                    mode = { props.mode }
                    onSubmit = { ( { values, isValid } ) => {
                        if ( isValid ) {
                            props.onFormSectionSubmit( values, props.subFields );
                        }
                    } }
                >
                    { ( { fields, values, /* isValid,*/ errors, onChange, onSumbit } = { } ) => {

                        return (

                            <div
                                className = 'ui-form-section__form'
                            >

                                { /* fields */ }
                                { map( fields, ( field ) => {

                                    if( get( field, 'config.hide' ) ) {
                                        return null;
                                    }

                                    const formSectionFieldClass = classNames( 'ui-form-section__form__field', `ui-form-section__form__field--${ field.name }` );

                                    return (
                                        <field.view
                                            key = { field.name }
                                            className = { formSectionFieldClass }
                                            name = { field.name }
                                            required = { field.required }
                                            placeholder = { field.placeholder }
                                            label = { field.title }
                                            onChange = { onChange }
                                            value = { values[ field.name ] }
                                            error = { errors[ field.name ] }
                                            allowFilter = { 'state' === field.name }
                                            config = { field.config }
                                            options = { get( props.options, field.options ) }
                                            closePopoverOnScrollTrigger={ props.closePopoverOnScrollTrigger }
                                            mask={ field.mask }
                                        />
                                    );
                                } ) }

                                { /* button actions */ }

                                <div className = 'ui-form-section__buttons'>
                                    <Button
                                        type = { 'button' }
                                        outline = { false }
                                        size = 'medium'
                                        className = 'ui-form-section__buttons__add-button'
                                        onClick = { onSumbit }
                                        title = { props.addButtonTitle }
                                        key = { 'add-button' }
                                    />

                                    <Button
                                        type = { 'button' }
                                        disabled = { false }
                                        outline = { true }
                                        size = 'medium'
                                        onClick = { () => isEmpty( props.editableValues ) ? props.onCancelClick() : props.onEditFormCancelClick() }
                                        title = { props.cancelButtonTitle }
                                        key = { 'cancel-button' }
                                    />
                                </div>
                            </div>
                        );
                    } }
                </Form>
                : null }

            <Table
                { ...props }
            />

            { /* error message */ }
            {
                hasError && !isEqual( props.mode, 'readonly' ) ? (
                    <div className = 'ui-input-field__error'>
                        <div className = 'ui-input-field__error__message'>
                            { getLabel( errorMessage ) }
                        </div>
                    </div>
                ) : null
            }

        </div>
    );
 };

// set display name
formSection.displayName = 'formSection';

// set default props
formSection.defaultProps = { };

// prop types
formSection.propTypes = { };
